/**
 * Zod schema for the profile-completion questionnaire — v2 (May 2026).
 *
 * Mirrors the "Frontend Dashboard Guide: Profile Update And Onboarding
 * Questionnaires" backend spec. Four customer types, each with its own
 * branch:
 *
 *   business_owner →  entity_structure (5 vals incl. `other`),
 *                     annual_turnover_range (4 vals),
 *                     services_required (5 vals, `none` exclusive),
 *                     entity_structure_other_detail / service_other_detail
 *
 *   professional  →   entity_structure (3 vals — different from business_owner!),
 *                     annual_income_range (4 vals — professional set),
 *                     services_required (5 vals, `none` exclusive),
 *                     service_other_detail
 *
 *   salaried      →   annual_income_range (4 vals — salaried set),
 *                     income_exposures (5 vals, `none` exclusive),
 *                     services_required (5 vals, `any_other` triggers detail),
 *                     other_exposure_detail / service_other_detail
 *
 *   others        →   other_customer_category (5 vals incl. `other`),
 *                     services_required (4 vals, `any_other` triggers detail),
 *                     other_customer_category_detail / service_other_detail
 *
 * Plus always-required: pan, dob, eri_consent, customer_type.
 *
 * Retired in v2: `business_service_interests`, `compliance_signals`,
 * `other_income`. Removed entirely so we don't accidentally send them.
 */
import { z } from 'zod';

// PAN_REGEX no longer needed at onboarding — kept here in case other
// callers import this file (none do today).
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]$/;

// ── Branch-specific enum supersets ─────────────────────────────────────────
// Values overlap minimally; we use one Zod enum per shared field and rely on
// superRefine to enforce per-branch validity. Backend is the source of truth.

const SALARIED_INCOME_VALUES = ['below_25l', '25_50l', '50l_2cr', 'above_2cr'] as const;
const PRO_INCOME_VALUES      = ['below_20l', '20_75l', '75l_2cr', 'above_2cr'] as const;
const TURNOVER_VALUES        = ['below_2cr', '2_10cr', '10_50cr', 'above_50cr'] as const;

// Union of all branches that emit `annual_income_range`.
const INCOME_RANGE_VALUES = [
  ...SALARIED_INCOME_VALUES,
  ...PRO_INCOME_VALUES,
] as const;

// Union of all entity_structure variants across business_owner + professional.
const ENTITY_STRUCTURE_VALUES = [
  // business_owner
  'proprietorship', 'partnership_firm', 'llp', 'private_limited_company', 'other',
  // professional
  'solo_proprietorship', 'partnership_llp', 'opc_private_limited',
] as const;

// Salaried-only.
const INCOME_EXPOSURE_VALUES = [
  'esop_rsu', 'foreign_income_assets', 'capital_gains', 'any_other', 'none',
] as const;

// services_required is highly branch-specific; we accept the full union and
// let the branch superRefine narrow.
const SERVICES_REQUIRED_VALUES = [
  // business_owner
  'bookkeeping_pl_management', 'compliance_related', 'registrations',
  // professional
  'compliance_filing', 'bookkeeping_advance_tax', 'portfolio_planning',
  // salaried
  'itr_filing', 'advance_tax_planning', 'huf_registration_planning', 'portfolio_tracking',
  // others
  'tax_planning', 'epf_withdrawal',
  // shared sentinels
  'any_other', 'none',
] as const;

// others-branch only.
const OTHER_CUSTOMER_CATEGORY_VALUES = [
  'nri_oci', 'retired_individual', 'society_association', 'trust', 'other',
] as const;

// Helper — checks for backend's "none cannot combine with any other" rule.
function violatesNoneExclusive(values: readonly string[] | undefined): boolean {
  if (!values || values.length <= 1) return false;
  return values.includes('none');
}

// ── Schema ───────────────────────────────────────────────────────────────────

export const questionnaireSchema = z
  .object({
    customer_type: z.enum(['salaried', 'professional', 'business_owner', 'others'], {
      required_error: 'Please select your customer type',
    }),

    annual_income_range:   z.enum(INCOME_RANGE_VALUES).optional(),
    annual_turnover_range: z.enum(TURNOVER_VALUES).optional(),

    entity_structure:               z.enum(ENTITY_STRUCTURE_VALUES).optional(),
    entity_structure_other_detail:  z.string().max(500).optional(),

    income_exposures:      z.array(z.enum(INCOME_EXPOSURE_VALUES)).optional(),
    other_exposure_detail: z.string().max(500).optional(),

    services_required:    z.array(z.enum(SERVICES_REQUIRED_VALUES)).optional(),
    service_other_detail: z.string().max(500).optional(),

    other_customer_category:        z.enum(OTHER_CUSTOMER_CATEGORY_VALUES).optional(),
    other_customer_category_detail: z.string().max(500).optional(),

    // PAN / DOB / ERI consent / portal password are NOT collected at
    // onboarding in the v2 spec. PAN + DOB are gathered at service
    // creation when missing from the profile (see IntakeForm).
  })
  .superRefine((data, ctx) => {
    // ── Salaried ──────────────────────────────────────────────────────────
    if (data.customer_type === 'salaried') {
      if (
        !data.annual_income_range ||
        !(SALARIED_INCOME_VALUES as readonly string[]).includes(data.annual_income_range)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['annual_income_range'],
          message: 'Please select your annual income range',
        });
      }

      // income_exposures ≥1, "none" exclusive
      if (!data.income_exposures || data.income_exposures.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['income_exposures'],
          message: 'Please select at least one option',
        });
      } else if (violatesNoneExclusive(data.income_exposures)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['income_exposures'],
          message: '"None of the above" can\'t be combined with other options',
        });
      }

      if (!data.services_required || data.services_required.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['services_required'],
          message: 'Please pick at least one service',
        });
      }

      if (
        data.income_exposures?.includes('any_other') &&
        (!data.other_exposure_detail || data.other_exposure_detail.trim().length === 0)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['other_exposure_detail'],
          message: 'Please describe your "any other" income',
        });
      }

      if (
        data.services_required?.includes('any_other') &&
        (!data.service_other_detail || data.service_other_detail.trim().length === 0)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['service_other_detail'],
          message: 'Please describe what other service you need',
        });
      }
    }

    // ── Professional ──────────────────────────────────────────────────────
    if (data.customer_type === 'professional') {
      if (!data.entity_structure) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['entity_structure'],
          message: 'Please select your practice structure',
        });
      }
      if (
        !data.annual_income_range ||
        !(PRO_INCOME_VALUES as readonly string[]).includes(data.annual_income_range)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['annual_income_range'],
          message: 'Please select your annual income range',
        });
      }
      if (!data.services_required || data.services_required.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['services_required'],
          message: 'Please pick at least one service',
        });
      } else if (violatesNoneExclusive(data.services_required)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['services_required'],
          message: '"None" can\'t be combined with other options',
        });
      }

      if (
        data.services_required?.includes('any_other') &&
        (!data.service_other_detail || data.service_other_detail.trim().length === 0)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['service_other_detail'],
          message: 'Please describe what other service you need',
        });
      }
    }

    // ── Business Owner ────────────────────────────────────────────────────
    if (data.customer_type === 'business_owner') {
      if (!data.entity_structure) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['entity_structure'],
          message: 'Please select your entity structure',
        });
      }
      if (!data.annual_turnover_range) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['annual_turnover_range'],
          message: 'Please select your annual turnover range',
        });
      }
      if (!data.services_required || data.services_required.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['services_required'],
          message: 'Please pick at least one service',
        });
      } else if (violatesNoneExclusive(data.services_required)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['services_required'],
          message: '"None" can\'t be combined with other options',
        });
      }

      if (
        data.entity_structure === 'other' &&
        (!data.entity_structure_other_detail || data.entity_structure_other_detail.trim().length === 0)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['entity_structure_other_detail'],
          message: 'Please describe your entity structure',
        });
      }

      if (
        data.services_required?.includes('any_other') &&
        (!data.service_other_detail || data.service_other_detail.trim().length === 0)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['service_other_detail'],
          message: 'Please describe what other service you need',
        });
      }
    }

    // ── Others ────────────────────────────────────────────────────────────
    if (data.customer_type === 'others') {
      if (!data.other_customer_category) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['other_customer_category'],
          message: 'Please select the category that best describes you',
        });
      }
      if (
        data.other_customer_category === 'other' &&
        (!data.other_customer_category_detail || data.other_customer_category_detail.trim().length === 0)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['other_customer_category_detail'],
          message: 'Please describe your category',
        });
      }
      if (!data.services_required || data.services_required.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['services_required'],
          message: 'Please pick at least one service',
        });
      }
      if (
        data.services_required?.includes('any_other') &&
        (!data.service_other_detail || data.service_other_detail.trim().length === 0)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['service_other_detail'],
          message: 'Please describe what other service you need',
        });
      }
    }
  });

export type QuestionnaireInput = z.infer<typeof questionnaireSchema>;
